import React, { useState, useEffect } from 'react'
import { Global } from '@emotion/react'
import { styled } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'

import * as Styled from './styles'

import { APP_HEADER_FIXED_HEIGHT } from '../AppHeader/styles'

const BOUNCE_ANIMATION_DURATION_MS = 600

const Root = styled('div')(() => ({
  height: '100%',
}))

const StyledBox = styled('div')<{ shouldBounce: boolean }>(({ shouldBounce }) => ({
  animation: shouldBounce ? `bounce ${BOUNCE_ANIMATION_DURATION_MS}ms ease-out` : 'none',
  '@keyframes bounce': {
    '0%, 100%': {
      transform: 'translateY(0)',
    },
    '25%': {
      transform: 'translateY(-20px)',
    },
    '50%': {
      transform: 'translateY(0)',
    },
    '75%': {
      transform: 'translateY(-10px)',
    },
  },
}))

interface Props {
  children?: React.ReactNode
  open: boolean
  onClose: () => void
  onOpen: () => void
}

const SwipeableEdgeDrawer: React.FC<Props> = ({ children, open, onClose, onOpen }) => {
  const [drawerBleeding, setDrawerBleeding] = useState(window.innerWidth < 768 ? 160 : 190)
  const [shouldBounce, setShouldBounce] = useState(false)

  useEffect(() => {
    const handleResize = (): void => {
      setDrawerBleeding(window.innerWidth < 768 ? 160 : 190)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    const hasBouncedKey = 'mok_swipeableEdgeDrawer_bounced'

    if (!sessionStorage.getItem(hasBouncedKey)) {
      const delayTimer = setTimeout(() => {
        setShouldBounce(true)
        // Reset bounce animation after it completes and mark as bounced
        setTimeout(() => {
          setShouldBounce(false)
          sessionStorage.setItem(hasBouncedKey, 'true')
        }, BOUNCE_ANIMATION_DURATION_MS)
      }, 1000)

      return () => clearTimeout(delayTimer)
    }
  }, [])

  return (
    <Root>
      <CssBaseline />
      <Global
        // @ts-expect-error // TODO
        styles={{
          '.MuiDrawer-root': {
            position: 'static !important',
          },
          '.MuiDrawer-root > .MuiPaper-root': {
            height: `calc(100vh - 80px - ${APP_HEADER_FIXED_HEIGHT} -  ${drawerBleeding}px)`,
            overflow: 'visible',
          },
        }}
      />
      <SwipeableDrawer
        container={window.document.body}
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={onOpen}
        swipeAreaWidth={drawerBleeding}
        disableSwipeToOpen={false}
        allowSwipeInChildren={false} // needs to be false so that user can interact with the model
        ModalProps={{
          keepMounted: true,
        }}
      >
        <StyledBox
          className={open ? 'MuiDrawer-open' : ''}
          shouldBounce={shouldBounce}
          // For refrence see this link https://mui.com/system/getting-started/the-sx-prop/
          sx={{
            position: 'absolute',
            top: -drawerBleeding,
            visibility: 'visible',
            right: 0,
            left: 0,
            backgroundColor: 'transparent',
          }}
        >
          {children}
          <Styled.Puller />
        </StyledBox>
      </SwipeableDrawer>
    </Root>
  )
}

export default SwipeableEdgeDrawer
